/**
 * uploadAsset — the builder's authenticated ASSET uploader (spec 0020, Slice 0/2 · §3-HQ2).
 *
 * The image/video controls used to embed a base64 `data:` URL inline in the JSONB page document
 * (imageUpload.ts stub). That is fine for a 15 MB image but untenable for video (a 30 MB clip ≈ 40 MB
 * base64 on every debounced autosave). This uploader POSTs the file to the backend asset module and
 * returns the stored delivery URL (`/api/assets/public/<key>`) — the value stored in the block is a
 * URL, never bytes.
 *
 * Two transports, one auth policy (both reuse `withAuthRetry` from http.ts — Bearer + single-flight
 * refresh + one-401 replay; §3-HQ2):
 *   - IMAGE → multipart `FormData` to `POST /api/assets/images` (the browser sets the multipart
 *     boundary, so we must NOT set Content-Type ourselves).
 *   - VIDEO → the raw file as an `application/octet-stream` body to `POST /api/assets/videos`. This is
 *     the streaming, NON-multipart transport (spec §5c/B1): it sidesteps the container-global multipart
 *     size cap and lets the backend enforce the 30 MB limit while copying, with no heap buffering.
 *
 * Tenancy: no `store_id` is ever sent — the backend derives it from the JWT (build-plan §10.1).
 */
import { bt, btp } from '../i18n';
import { prepareImageForUpload } from '@aquilixshop/media';

import { getToken } from '../boot/session';
import { deriveApiBaseUrl } from '../boot/config';
import { withAuthRetry } from './http';
import { HttpError, NetworkError } from './errors';

/** The backend's upload response (`ImageUploadResponse` / `VideoUploadResponse`). */
export interface UploadedAsset {
  /** Delivery URL: `<publicBaseUrl>/api/assets/public/<key>`. */
  url: string;
  width?: number;
  height?: number;
  /** Seconds — present for video only. */
  duration?: number;
  /**
   * Video only: delivery URL of a still the SERVER grabbed out of the clip with ffmpeg. When present it
   * replaces the browser-side capture — it is guaranteed (the browser one failed silently on real seller
   * clips) and it spares the seller a second upload over their uplink. Absent when the server has no
   * ffmpeg (local dev) or could not decode a frame.
   */
  posterUrl?: string;
  /**
   * Video only: delivery URL of a phone-sized (640 long edge) rendition of the same clip. Stored as an
   * extra `sources[]` entry so the storefront engine can hand a phone the small file instead of the
   * desktop one. Absent when a second copy was not worth producing (clip already small, or no ffmpeg).
   */
  smallUrl?: string;
  /**
   * Video only: the picture size of the rendition at `smallUrl`, as the SERVER probed it on the file it
   * stored.
   *
   * Never assume 640. The encoder scales the LONG edge, so a portrait clip's phone rendition is 360x640 —
   * and the builder used to write `width: 640` for it regardless. The storefront picks the rendition to
   * download by comparing that width to the box the clip is painted in, so it handed a 360 px file to a box
   * asking for up to 640 and the visitor watched an upscale, while the seller's own editor — which previews
   * their local file — showed the clip pristine. Absent when the server has no ffmpeg.
   */
  smallWidth?: number;
  smallHeight?: number;
  /**
   * Video only: delivery URL of the MIDDLE rung — 1280 px on the long edge.
   *
   * Between the phone rung and the stored file there used to be nothing, so every visitor who was not on a
   * phone had exactly one option: the biggest file on the shelf. Measured on a live storefront hero, that
   * was 4.18 MB for a four-second clip where 200 kB covers the box — the "the video is too slow on
   * desktop, it freezes" report. Absent when the clip was already at or near that size.
   */
  midUrl?: string;
  /** The picture size of `midUrl`, probed by the server. Scaled by the LONG edge too: a portrait clip is 960x1280. */
  midWidth?: number;
  midHeight?: number;
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

/**
 * One authenticated upload attempt, with the shared 401-replay policy.
 *
 * `transport` lets the VIDEO path swap `fetch` for XMLHttpRequest (the only API that reports upload
 * progress) while reusing this exact auth + error-envelope handling. An aborted upload is re-thrown as-is
 * so the caller can tell "the seller cancelled" apart from "the network died".
 */
async function send(
  url: string,
  init: () => RequestInit,
  transport?: () => Promise<Response>,
): Promise<UploadedAsset> {
  const res = await withAuthRetry(async () => {
    try {
      return transport ? await transport() : await fetch(url, init());
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      if (e instanceof NetworkError) throw e;
      throw new NetworkError();
    }
    // Uploads are always writes — the read-only guard in withAuthRetry blocks them in consultation.
  }, 'POST');
  const text = await res.text();
  let body: unknown = undefined;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    throw new HttpError(res.status, refusalMessage(res.status, body), body);
  }
  return (body ?? {}) as UploadedAsset;
}

/**
 * The line the seller reads when the server REFUSES an upload — picked from the error CODE, in their
 * language.
 *
 * It used to be the envelope's `message`, which the backend writes in English ("That image is too large
 * (max 8 MB).") and hands to whoever asked. A French seller therefore read an English sentence in a French
 * editor — and a stale one: that cap has been 15 MB for a while, and nothing watches a sentence that lives
 * on the server. The CODE is the stable half of the envelope, so the wording is chosen here, beside every
 * other word in this editor, where the translation gate can see it.
 *
 * An unknown code falls back to the generic line WITH the status: a number the seller can quote to us is
 * worth more than a confident sentence about the wrong cause.
 */
function refusalMessage(status: number, body: unknown): string {
  const code =
    typeof body === 'object' && body !== null && 'code' in body
      ? String((body as { code: unknown }).code)
      : '';
  switch (code) {
    case 'ASSET_TOO_LARGE':
      return bt('chrome.media.image_too_large', 'That image is too large (max 15 MB).');
    case 'ASSET_VIDEO_TOO_LARGE':
      return bt(
        'chrome.media.video_too_large',
        'That video is too large (max 30 MB). Compress it or trim it shorter.',
      );
    case 'ASSET_UNSUPPORTED_TYPE':
      return bt(
        'chrome.media.image_type',
        'Unsupported image type. Use PNG, JPEG, WebP, GIF, AVIF or HEIC.',
      );
    case 'ASSET_UNSUPPORTED_VIDEO':
      return bt('chrome.media.video_type', 'Unsupported video type. Use MP4 or WebM.');
    case 'ASSET_DECODE_FAILED':
      return bt(
        'chrome.err.upload_decode_failed',
        "That file couldn't be processed. Try another one.",
      );
    case 'ASSET_RATE_LIMITED':
      return bt(
        'chrome.err.upload_rate_limited',
        'Too many uploads in a row. Wait a moment, then try again.',
      );
    default:
      break;
  }
  if (status >= 500) {
    return bt(
      'chrome.err.upload_server',
      "The server couldn't store that file. Try again in a moment.",
    );
  }
  return btp('chrome.err.upload_failed', 'Upload failed ({status})', { status });
}

export interface ImageUploadOptions {
  /** Called with 0-100 as the bytes leave the machine. */
  onProgress?: (percent: number) => void;
  /** Abort the in-flight upload; the promise rejects with an `AbortError`. */
  signal?: AbortSignal;
  /** Called with the attempt number (2, 3, …) when a dropped connection is being retried. */
  onRetry?: (attempt: number) => void;
  /**
   * Called once the file has been shrunk, BEFORE a byte is sent — with the bytes actually going out and
   * the bytes the seller picked. The control uses it to show "6,2 Mo → 310 Ko" instead of a mute spinner.
   */
  onPrepared?: (info: { bytes: number; originalBytes: number }) => void;
}

/**
 * Upload an image → stored delivery URL + dims.
 *
 * Two things happen before the network is touched. First the file is SHRUNK to the very rendition the
 * server would have stored anyway (2560px WebP, see `@aquilixshop/media`) — measured 811 KB → 72 KB on a
 * real photo at phone size, i.e. seconds off the wait on a home uplink — and on the server those bytes
 * land on the pass-through fast path instead of a second decode/re-encode. Then it goes
 * out over XHR rather than `fetch`, for the same reason video does: `fetch` cannot report upload progress,
 * so the control had nothing to show but a frozen spinner. A dropped connection is retried with the same
 * bytes ([UPLOAD_NETWORK_RETRIES]) instead of leaving the seller to re-pick the file themselves.
 */
export async function uploadImage(
  file: File,
  opts: ImageUploadOptions = {},
): Promise<UploadedAsset> {
  const url = `${deriveApiBaseUrl()}/api/assets/images`;
  const prepared = await prepareImageForUpload(file);
  opts.onPrepared?.({ bytes: prepared.bytes, originalBytes: prepared.originalBytes });
  // The bytes, in memory, ONCE — never the `File` handle. See [snapshotImage]; video has done this since
  // the same failure was traced there.
  const part = await snapshotImage(prepared.file, prepared.recompressed);
  return send(
    url,
    () => ({ method: 'POST' }),
    // Rebuilt per attempt: FormData is a one-shot body and the 401-replay sends it again.
    () => {
      const fd = new FormData();
      fd.append('file', part);
      // NOTE: no Content-Type header — the browser must add the multipart boundary itself.
      return sendXhrWithRetry(url, fd, opts, {});
    },
  );
}

/** What the XHR transport itself needs — the intersection of the image and video option shapes. */
interface TransportOptions {
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
  onRetry?: (attempt: number) => void;
}

export interface VideoUploadOptions {
  /** Called with 0-100 as the bytes leave the machine. */
  onProgress?: (percent: number) => void;
  /** Abort the in-flight upload; the promise rejects with an `AbortError`. */
  signal?: AbortSignal;
  /** Called with the attempt number (2, 3, …) when a dropped connection is being retried. */
  onRetry?: (attempt: number) => void;
  /**
   * How hard the server may squeeze the clip. Default `'standard'` — what the pipeline always did.
   *
   * This replaces a boolean, and the reason is the gap the boolean left. `'standard'` caps the picture at
   * 720p and spends CRF 26 on it, which scores VMAF 82 on a detail-rich product plan — softness a customer
   * studying a shoe can see. The only way to refuse it was `'original'`, which stores the seller's own
   * bytes and cuts NO phone rendition at all. So the choice was "soft" or "heavy for every visitor".
   *
   * `'detail'` is the middle: 1080p at CRF 21 (VMAF 95 on that same plan, in fewer bytes than 720p/21
   * takes), and it still cuts a phone rendition. See the backend's `VideoQuality` for the full table.
   */
  quality?: VideoQuality;
}

/**
 * The three tiers, spelled as the backend's `?quality=` parameter expects them.
 *
 * Lower-case on the wire because a query string is a URL, not a Kotlin enum; the server parses it
 * case-insensitively anyway and falls back to `standard` on anything it does not know — an upload must
 * never fail because a tier was spelled oddly.
 */
export type VideoQuality = 'standard' | 'detail' | 'original';

/**
 * How many EXTRA attempts a dropped connection gets. A video upload is not a 200 ms API call: measured on
 * preprod 2026-07-31, a 13.4 MB clip is ~69 s on a home uplink (~194 KB/s) and the 30 MB cap is ~2.5 min.
 * Over a window that long an intermittent connection is ordinary, and the seller was the retry loop —
 * they re-picked the same file until one attempt happened to survive. The transport itself is fine
 * (6 MB → 201 in 26 s from Chrome in the same conditions), so retrying the same bytes is the right answer.
 */
const UPLOAD_NETWORK_RETRIES = 2;
const UPLOAD_RETRY_BACKOFF_MS = 1_500;

/**
 * Upload a video as a raw octet-stream body → stored delivery URL + dims + duration (Slice 1/2).
 *
 * Uses XMLHttpRequest, NOT `fetch`, for one reason: **the Fetch API cannot report upload progress.** It only
 * resolves once the server has answered, so the control could show nothing but a frozen "Uploading…". A
 * 29 MB clip measured at ~47 s against the real backend — three quarters of a minute of a UI that looks
 * hung, with no way to cancel. `xhr.upload.onprogress` is the only browser API that reports it.
 *
 * The auth policy is unchanged: the response is adapted into a real `Response` so it still flows through
 * `withAuthRetry` (Bearer + single-flight refresh + one-401 replay), and the headers are rebuilt per attempt
 * so a replay after a refresh carries the NEW token.
 */
export async function uploadVideo(
  file: File,
  opts: VideoUploadOptions = {},
): Promise<UploadedAsset> {
  // The tier rides in the QUERY STRING, not a header: the body is the raw file (octet-stream), so there is
  // no other place to put it, and it stays visible in the network panel when a seller reports a soft clip.
  // `standard` is sent as no parameter at all rather than `?quality=standard`, so the default request is
  // byte-for-byte the one this endpoint has always received.
  const quality = opts.quality ?? 'standard';
  const url =
    `${deriveApiBaseUrl()}/api/assets/videos` +
    (quality === 'standard' ? '' : `?quality=${quality}`);
  // Snapshotted ONCE, out here: `send` re-invokes the transport on a 401 replay, and re-reading the file
  // then would put the "it moved on disk" failure back on the table.
  const body = await snapshot(file);
  return send(
    url,
    () => ({ method: 'POST' }),
    () => sendXhrWithRetry(url, body, opts, { 'Content-Type': 'application/octet-stream' }),
  );
}

/**
 * `sendXhr` plus [UPLOAD_NETWORK_RETRIES] retries on a DROPPED CONNECTION only.
 *
 * Retried: `NetworkError` — the transport died (xhr status 0). Not retried: every HTTP answer, because
 * `sendXhr` resolves those as a `Response` and the layers above own them (401 → refresh + replay,
 * 429 → the server's "slow down" message, 400 → the real reason). Not retried either: an abort, which is
 * the seller pressing Cancel.
 *
 * A failure that survives every attempt is re-thrown with the point it died at, so the panel stops saying
 * a bare "Network request failed." at a seller who cannot act on it.
 */
async function sendXhrWithRetry(
  url: string,
  body: XMLHttpRequestBodyInit,
  opts: TransportOptions,
  headers: Record<string, string>,
): Promise<Response> {
  let reached = 0;
  const track: TransportOptions = {
    ...opts,
    onProgress: (p) => {
      reached = p;
      opts.onProgress?.(p);
    },
  };
  for (let attempt = 1; ; attempt++) {
    try {
      return await sendXhr(url, body, track, headers);
    } catch (e) {
      const retryable = e instanceof NetworkError && !opts.signal?.aborted;
      if (!retryable) throw e;
      if (attempt > UPLOAD_NETWORK_RETRIES) {
        // One line. The seller does not need our attempt count or a lecture on connections — they need to
        // know it stopped, roughly where, and that pressing the button next to this will resume the job.
        throw new NetworkError(
          btp('chrome.err.upload_stopped', 'Upload stopped at {percent}%.', { percent: reached }),
        );
      }
      opts.onRetry?.(attempt + 1);
      opts.onProgress?.(0);
      await sleep(UPLOAD_RETRY_BACKOFF_MS, opts.signal);
    }
  }
}

/**
 * Read the file into memory ONCE, and upload that instead of the `File` handle.
 *
 * A `File` is a lazy reference to bytes on disk, not the bytes. Chrome re-reads it while the request is in
 * flight, so if the file is still being written, gets moved, renamed or replaced meanwhile, the read fails
 * mid-request and the XHR dies with status 0 — indistinguishable from a network drop, and looking to the
 * seller exactly like "the upload cancelled itself". That is not hypothetical here: the clips come out of
 * ~/Downloads, where a file can very well still be arriving when it is picked.
 *
 * It also makes the retry above meaningful: re-sending a `File` whose disk state moved fails again every
 * time, while re-sending a snapshot succeeds. ≤30 MB (the server cap), freed as soon as the upload ends.
 *
 * A read that fails HERE is reported for what it is, instead of being laundered into "network".
 */
async function snapshot(file: File): Promise<Blob> {
  try {
    return new Blob([await file.arrayBuffer()], { type: 'application/octet-stream' });
  } catch {
    throw new NetworkError(
      bt('chrome.err.file_unreadable', 'Couldn’t read that file — is it still downloading?'),
    );
  }
}

/**
 * The image part, as BYTES HELD IN MEMORY rather than a reference to a file on disk.
 *
 * A `File` is a lazy handle, and the browser re-reads it while the request is in flight. When the shrink
 * step succeeded the part is already an in-memory blob the canvas produced, so there is nothing to do; the
 * moment it stands down — an animated GIF, an already-conformant WebP, a browser with no encoder, a decode
 * that threw — the part IS the seller's file on disk, and everything that can happen to a file between the
 * drop and the last byte becomes an upload failure with no name: the file is still downloading (these come
 * out of ~/Downloads by the handful), it gets moved, renamed, replaced by the next generation. Chrome then
 * kills the XHR with status 0 — indistinguishable from a dropped connection — so the seller is told the
 * network failed, and both automatic retries re-read the same moving file and die the same way.
 *
 * `uploadVideo` has snapshotted its body for exactly this reason since 2026-09-16; the image path was left
 * on the disk handle. Holding the bytes also makes "Try again" meaningful: it re-sends what we captured,
 * not whatever is at that path now.
 */
async function snapshotImage(file: File, recompressed: boolean): Promise<File> {
  if (recompressed) return file; // already a fresh blob off the canvas — nothing on disk to move
  try {
    return new File([await file.arrayBuffer()], file.name, {
      type: file.type || 'application/octet-stream',
    });
  } catch {
    throw new NetworkError(
      bt('chrome.err.file_unreadable', 'Couldn’t read that file — is it still downloading?'),
    );
  }
}

/** Wait, but wake up immediately (and reject) if the seller cancels during the backoff. */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Upload cancelled', 'AbortError'));
      },
      { once: true },
    );
  });
}

/** Drive one XHR upload and adapt its outcome to a `Response` so `withAuthRetry` can inspect the status. */
function sendXhr(
  url: string,
  body: XMLHttpRequestBodyInit,
  opts: TransportOptions,
  headers: Record<string, string>,
): Promise<Response> {
  return new Promise((resolve, reject) => {
    if (opts.signal?.aborted) {
      reject(new DOMException('Upload cancelled', 'AbortError'));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.withCredentials = true;
    // setRequestHeader is only legal after open(); the token is read HERE so a retry gets the refreshed one.
    Object.entries(authHeaders(headers)).forEach(([k, v]) => xhr.setRequestHeader(k, v));

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && opts.onProgress) {
        opts.onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
      }
    };
    xhr.onload = () => {
      // status 0 means the transport failed (or was aborted) — `new Response` would throw on it.
      if (xhr.status === 0) {
        reject(new NetworkError());
        return;
      }
      resolve(new Response(xhr.responseText, { status: xhr.status }));
    };
    xhr.onerror = () => reject(new NetworkError());
    xhr.ontimeout = () => reject(new NetworkError());
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));

    opts.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}
